import React from 'react';
import { wrapCreateBrowserRouter } from '@sentry/react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import AuthLayout from 'common/layouts/auth/auth-layout.component';
import MainLayout from 'common/layouts/main/main-layout.component';
import NoMatchPage from 'pages/404/404.page';
import AccountManagementCounterpartyEmissionPage, {
  loader as accountManagementCounterpartyEmissionPageLoader,
} from 'pages/account-management/counterparty-emission/counterparty-emission.page';
import AccountManagementCounterpartyProcessingPage, {
  loader as accountManagementCounterpartyProcessingPageLoader,
} from 'pages/account-management/counterparty-processing/counterparty-processing.page';
import AccountManagementViewCustomersPage, {
  loader as AccountManagementCustomersPageLoader,
} from 'pages/account-management/customers/customers.page';
import AccountManagementLayout from 'pages/account-management/layout/account-management.layout';
import AssetsPage, {
  loader as assetsPageLoader,
} from 'pages/assets/assets/assets.page';
import AssetsLayout from 'pages/assets/layout/assets.layout';
import ConfirmOtpCodePage from 'pages/auth/confirm-code/confirm-code.page';
import CreateNewPasswordPage from 'pages/auth/create-new-password/create-new-password.page';
import LoginPage, { loader as loginPageLoader } from 'pages/auth/login/login.page';
import ResetPasswordPage from 'pages/auth/reset-password/reset-password.page';
import DashboardPage, { loader as dashboardPageLoader } from 'pages/dashboard/dashboard.page';
import LimitsIndividualLimitsPage, {
  loader as individualLimitsPageLoader,
} from 'pages/limits/individual-limits/individual-limits.page';
import LimitsLayout from 'pages/limits/layout/layout/limits.layout';
import StandardLimitsPage, {
  loader as standardLimitsPageLoader,
} from 'pages/limits/standard-limits/standard-limits.page';
import PaymentOperationLayout from 'pages/payment-operation/layout/payment-operation.layout';
import PaymentOperationPage, {
  loader as loaderPaymentOperationPage,
} from 'pages/payment-operation/payment-operation/payment-operation.page';
import PaymentStatusPage from 'pages/payment-operation/payment-status/payment-status.page';
import ReconciliationLayout from 'pages/reconciliation/layout/reconciliation.layout';
import StatisticPage, {
  loader as statisticReconciliationPageLoader,
} from 'pages/reconciliation/statistic/statistic.page';
import ReportingAuditPage, {
  loader as reportingAuditPageLoader,
} from 'pages/reporting/audit/audit.page';
import ReportingEmissionHistoryPage from 'pages/reporting/emission-history/emission-history.page';
import ReportingLayout from 'pages/reporting/layout/reporting.layout';
import ReportingPaymentsPage, { loader as reportingPaymentsPageLoader } from 'pages/reporting/payments/payment.page';
import UserRoleManagementBranchRolesPage, {
  loader as userRoleManagementBranchRolesPageLoader
} from 'pages/user-role-management/branch-roles/branch-roles.page';
import UserRoleManagementBranchesPage, {
  loader as userRoleManagementBranchesPageLoader,
} from 'pages/user-role-management/branches/branches.page';
import UserRoleManagementCounterpartiesPage, {
  loader as userRoleManagementCounterpartiesPageLoader
} from 'pages/user-role-management/counterparties/counterparties.page';
import UserRoleManagementCustomersPage, {
  loader as userRoleManagementCustomersPageLoader,
} from 'pages/user-role-management/customers/customers.page';
import UserRoleManagementEmployeesPage, {
  loader as userRoleManagementEmployeesPageLoader,
} from 'pages/user-role-management/employees/employees.page';
import UserManagementLayout, {
  loader as userRoleManagementLayoutLoader,
} from 'pages/user-role-management/layout/user-role-management.layout';
import UserRoleManagementMerchantPage, {
  loader as userRoleManagementMerchantPageLoader
} from 'pages/user-role-management/merchants/merchants.page';
import UserRoleManagementPermissionsPage, {
  loader as userRoleManagementPermissionsPageLoader
} from 'pages/user-role-management/permissions/permissions.page';
import UserRoleManagementRolesPage, {
  loader as reportingRolesPageLoader,
} from 'pages/user-role-management/roles/roles.page';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import ProtectedRouteGuard from './guards/ProtectedRoute.guard';
import PublicRouteGuard from './guards/PublicRoute.guard';

const routes = (
  <Route
    path={ROUTES_MAPPING.ROOT}
    // This option allows you to unlock the React Query cache for a second request
    shouldRevalidate={({ currentUrl }): boolean => currentUrl.pathname === ROUTES_MAPPING.PUBLIC.LOGIN}
    errorElement={<ErrorBoundary />}
  >
    <Route index element={<Navigate to={ROUTES_MAPPING.PUBLIC.LOGIN} />} />
    <Route path={ROUTES_MAPPING.RESET_PASSWORD} element={<ResetPasswordPage />} />
    {/* Public routes */}
    <Route element={<PublicRouteGuard />}>
      <Route element={<AuthLayout />}>
        <Route path={ROUTES_MAPPING.PUBLIC.LOGIN} element={<LoginPage />} loader={loginPageLoader} />
        <Route path={ROUTES_MAPPING.PUBLIC.CONFIRM_CODE} element={<ConfirmOtpCodePage />} />
        <Route path={ROUTES_MAPPING.PUBLIC.CREATE_NEW_PASSWORD} element={<CreateNewPasswordPage />} />
      </Route>
    </Route>
    {/* Protected routes */}
    <Route element={<ProtectedRouteGuard />}>
      <Route element={<MainLayout />}>
        <Route path={ROUTES_MAPPING.PROTECTED.DASHBOARD} element={<DashboardPage />} loader={dashboardPageLoader} />
        <Route path={ROUTES_MAPPING.PROTECTED.PAYMENT} element={<PaymentOperationLayout />}>
          <Route index element={<Navigate to={ROUTES_MAPPING.PROTECTED.PAYMENT_OPERATION} />} />
          <Route
            element={<PaymentOperationPage />}
            loader={loaderPaymentOperationPage}
            path={ROUTES_MAPPING.PROTECTED.PAYMENT_OPERATION}
          />
          <Route element={<PaymentStatusPage />} path={ROUTES_MAPPING.PROTECTED.PAYMENT_STATUS} />
        </Route>
        <Route
          path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT}
          element={<UserManagementLayout/>}
          loader={userRoleManagementLayoutLoader}
        >
          <Route
            index
            element={
              <Navigate
                to={
                  IS_ADMIN_FE
                    ? ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES
                    : ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCHES
                }
              />
            }
          />
          <Route
            element={<UserRoleManagementBranchesPage />}
            loader={userRoleManagementBranchesPageLoader}
            path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCHES}
          />
          {IS_ADMIN_FE && (
            <Route
              element={<UserRoleManagementCounterpartiesPage />}
              loader={userRoleManagementCounterpartiesPageLoader}
              path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES}
            />
          )}
          <Route
            element={<UserRoleManagementEmployeesPage />}
            loader={userRoleManagementEmployeesPageLoader}
            path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_EMPLOYEES}
          />
          <Route
            element={<UserRoleManagementRolesPage />}
            loader={reportingRolesPageLoader}
            path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_ROLES}
          />
          <Route
            element={<UserRoleManagementBranchRolesPage />}
            loader={userRoleManagementBranchRolesPageLoader}
            path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCH_ROLES}
          />
          <Route
            element={<UserRoleManagementCustomersPage />}
            loader={userRoleManagementCustomersPageLoader}
            path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_CUSTOMERS}
          />
	        {IS_ADMIN_FE && (
            <>
              <Route
                element={<UserRoleManagementMerchantPage />}
                loader={userRoleManagementMerchantPageLoader}
                path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_MERCHANTS}
              />
              <Route
                element={<UserRoleManagementPermissionsPage />}
                loader={userRoleManagementPermissionsPageLoader}
                path={ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_PERMISSIONS}
              />
            </>
	        )}
        </Route>
        <Route path={ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT} element={<AccountManagementLayout />}>
          <Route index element={<Navigate to={ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_EMISSION} />} />
          <Route
            element={<AccountManagementCounterpartyEmissionPage />}
            loader={accountManagementCounterpartyEmissionPageLoader}
            path={ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_EMISSION}
          />
          <Route
            element={<AccountManagementCounterpartyProcessingPage />}
            loader={accountManagementCounterpartyProcessingPageLoader}
            path={ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_PROCESSING}
          />
          <Route
            element={<AccountManagementViewCustomersPage />}
            loader={AccountManagementCustomersPageLoader}
            path={ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS}
          />
        </Route>
        {IS_ADMIN_FE && (
          <Route path={ROUTES_MAPPING.PROTECTED.ASSETS} element={<AssetsLayout />}>
            <Route
              element={<AssetsPage />}
              loader={assetsPageLoader}
              path={ROUTES_MAPPING.PROTECTED.ASSETS}
            />
          </Route>
        )}
        <Route path={ROUTES_MAPPING.PROTECTED.REPORTING} element={<ReportingLayout />}>
          <Route index element={<Navigate to={ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS} />} />
          <Route
            path={ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS}
            element={<ReportingPaymentsPage />}
            loader={reportingPaymentsPageLoader}
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.REPORTING_EMISSION_HISTORY}
            element={<ReportingEmissionHistoryPage />}
          />
          {IS_ADMIN_FE && (
            <Route
              path={ROUTES_MAPPING.PROTECTED.REPORTING_AUDIT}
              loader={reportingAuditPageLoader}
              element={<ReportingAuditPage />}
            />
          )}
        </Route>
        <Route path={ROUTES_MAPPING.PROTECTED.LIMITS} element={<LimitsLayout />}>
          <Route index element={<Navigate to={ROUTES_MAPPING.PROTECTED.LIMITS_STANDARD_LIMITS} />} />
          <Route
            element={<StandardLimitsPage />}
            loader={standardLimitsPageLoader}
            path={ROUTES_MAPPING.PROTECTED.LIMITS_STANDARD_LIMITS}
          />
          <Route
            element={<LimitsIndividualLimitsPage />}
            loader={individualLimitsPageLoader}
            path={ROUTES_MAPPING.PROTECTED.LIMITS_INDIVIDUAL_LIMITS}
          />
        </Route>
        <Route path={ROUTES_MAPPING.PROTECTED.RECONCILIATION} element={<ReconciliationLayout />}>
          <Route index element={<Navigate to={ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC} />} />
          <Route
            path={ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC}
            element={<StatisticPage />}
            loader={statisticReconciliationPageLoader}
          />
        </Route>
      </Route>

      <Route path={ROUTES_MAPPING.PROTECTED.LIMITS} element={<LimitsLayout />}>
        <Route index element={<Navigate to={ROUTES_MAPPING.PROTECTED.LIMITS_STANDARD_LIMITS} />} />
        <Route
          element={<StandardLimitsPage />}
          loader={standardLimitsPageLoader}
          path={ROUTES_MAPPING.PROTECTED.LIMITS_STANDARD_LIMITS}
        />
        <Route
          element={<LimitsIndividualLimitsPage />}
          loader={individualLimitsPageLoader}
          path={ROUTES_MAPPING.PROTECTED.LIMITS_INDIVIDUAL_LIMITS}
        />
      </Route>
    </Route>
    <Route path={ROUTES_MAPPING.NO_MATCH} element={<AuthLayout />}>
      <Route index element={<NoMatchPage />} />
    </Route>
    <Route path="*" element={<Navigate to={ROUTES_MAPPING.NO_MATCH} />} />
  </Route>
);

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);
const routerTree = sentryCreateBrowserRouter(createRoutesFromElements(routes));

export default routerTree;
